import { getSelectStyles, getActionSelectStyles } from '../utility/selectStyles';
import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import axiosInstance from "../utility/axiosInstance";
import { FaSyncAlt, FaTrash, FaPlus } from 'react-icons/fa';
import { useToast } from "./ToastContext";
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';

interface LoadBalancerDetailsModalProps {
    isOpen: boolean;
    onClose: () => void;
    lbId: string;
}

const LoadBalancerDetailsModal: React.FC<LoadBalancerDetailsModalProps> = ({ isOpen, onClose, lbId }) => {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const [lb, setLb] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [addingMember, setAddingMember] = useState(false);
    const [selectedInstance, setSelectedInstance] = useState<any>(null);
    const [memberPort, setMemberPort] = useState(80);
    const [isSubmittingMember, setIsSubmittingMember] = useState(false);

    // Custom-domain / DNS management for this load balancer
    const [showDnsAdd, setShowDnsAdd] = useState(false);
    const [dnsDomain, setDnsDomain] = useState('');
    const [dnsPort, setDnsPort] = useState<number>(80);
    const [dnsSubmitting, setDnsSubmitting] = useState(false);
    const [verifyStep, setVerifyStep] = useState<'idle' | 'loading' | 'challenge' | 'verifying' | 'verified' | 'failed'>('idle');
    // `hint`: the record's value changed for this account (ROADMAP 91), when its pending claim is from before the change.
    const [challenge, setChallenge] = useState<{ record_name: string; record_value: string; hint?: string } | null>(null);

    const instances = useSelector((state: RootState) => state.instances.data);
    const securityGroups = useSelector((state: RootState) => state.securityGroups.data);
    const networks = useSelector((state: RootState) => state.privateNetwork.networks);

    const fetchDetails = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(`/v1/loadbalancer/${lbId}`);
            setLb(response.data);
        } catch (error) {
            console.error("Error fetching LB details:", error);
            showToast('Failed to fetch load balancer details', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen && lbId) {
            fetchDetails();
        }
    }, [isOpen, lbId]);

    const handleAddMember = async () => {
        if (!selectedInstance) return;

        // Validation check
        if (!selectedInstance.address || !selectedInstance.subnetId || !selectedInstance.securityGroupId) {
            showToast(`Missing network or security details for ${selectedInstance.label}. Try reloading the page.`, 'error');
            return;
        }
        
        setIsSubmittingMember(true);
        try {
            const poolId = lb.pools[0]?.id; // Assuming one pool for simplicity
            if (!poolId) throw new Error("No pool found on this load balancer");

            await axiosInstance.post('/v1/loadbalancer/members', {
                poolId,
                address: selectedInstance.address,
                protocolPort: memberPort,
                subnetId: selectedInstance.subnetId,
                securityGroupId: selectedInstance.securityGroupId,
                instanceId: selectedInstance.value,
                lbVipAddress: lb.vip_address
            });

            showToast('Member added successfully', 'success');
            fetchDetails();
            setAddingMember(false);
            setSelectedInstance(null);
        } catch (error) {
            console.error("Error adding member:", error);
            showToast('Failed to add member', 'error');
        } finally {
            setIsSubmittingMember(false);
        }
    };

    const handleRemoveMember = async (poolId: string, memberId: string, memberAddress: string) => {
        try {
            // Find instance ID by address to revert proxy
            const instance = instances.find(inst => 
                Object.values(inst.addresses || {}).flat().some((addr: any) => 
                    addr.addr === memberAddress && addr["OS-EXT-IPS:type"] === "fixed"
                )
            );

            await axiosInstance.delete(`/v1/loadbalancer/pools/${poolId}/members/${memberId}${instance ? `?instanceId=${instance.id}` : ''}`);
            showToast('Member removal initiated', 'success');
            fetchDetails();
        } catch (error) {
            console.error("Error removing member:", error);
            showToast('Failed to remove member', 'error');
        }
    };

    const handleCreateNPMRoute = async () => {
        if (!lb || !lb.vip_address) return;
        
        try {
            // First fetch an available port
            const portResponse = await axiosInstance.get('/v1/nginx/available-port');
            const availablePort = portResponse.data.port;

            await axiosInstance.post('/v1/nginx/create', {
                targetIp: lb.vip_address,
                targetPort: lb.listeners[0]?.protocol_port || 80,
                protocol: 'tcp',
                port: availablePort,
            });
            showToast(`NPM Proxy created successfully on port ${availablePort}`, 'success');
        } catch (error) {
            console.error("Error creating NPM route:", error);
            showToast('Failed to create NPM route', 'error');
        }
    };

    const isCustomDomain = dnsDomain.trim().length > 0 && !dnsDomain.trim().toLowerCase().endsWith('.redu.cloud');

    const generateDnsName = () => {
        const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
        const id = Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
        setDnsDomain(`lb-${id}.redu.cloud`);
        setVerifyStep('idle');
        setChallenge(null);
    };

    const dnsErr = (e: any, fallback: string) =>
        e.response?.data?.error?.message || e.response?.data?.message || e.response?.data?.error || fallback;

    const getChallenge = async () => {
        setVerifyStep('loading');
        try {
            const r = await axiosInstance.get(`/v1/dns/verify-domain?domain=${encodeURIComponent(dnsDomain.trim())}`);
            setChallenge(r.data);
            setVerifyStep('challenge');
        } catch (e: any) {
            showToast(dnsErr(e, 'Failed to load verification challenge'), 'error');
            setVerifyStep('idle');
        }
    };

    const verifyDomain = async () => {
        setVerifyStep('verifying');
        try {
            await axiosInstance.post('/v1/dns/verify-domain', { domain: dnsDomain.trim() });
            setVerifyStep('verified');
            showToast(`${dnsDomain.trim()} verified`, 'success');
        } catch (e: any) {
            showToast(dnsErr(e, 'Verification failed'), 'error');
            setVerifyStep('failed');
        }
    };

    const resetDnsForm = () => {
        setShowDnsAdd(false);
        setDnsDomain('');
        setVerifyStep('idle');
        setChallenge(null);
    };

    const addDns = async () => {
        setDnsSubmitting(true);
        try {
            await axiosInstance.post(`/v1/loadbalancer/${lbId}/dns`, { domain: dnsDomain.trim(), port: dnsPort });
            showToast('Domain added', 'success');
            resetDnsForm();
            fetchDetails();
        } catch (e: any) {
            showToast(dnsErr(e, 'Failed to add domain'), 'error');
        } finally {
            setDnsSubmitting(false);
        }
    };

    const removeDns = async (proxyId: number) => {
        try {
            await axiosInstance.delete(`/v1/loadbalancer/${lbId}/dns/${proxyId}`);
            showToast('Domain removed', 'success');
            fetchDetails();
        } catch (e: any) {
            showToast(dnsErr(e, 'Failed to remove domain'), 'error');
        }
    };

    const canSubmitDns = !dnsSubmitting && (!isCustomDomain || verifyStep === 'verified');

    const currentMemberAddresses = lb?.pools?.flatMap((p: any) => p.members?.map((m: any) => m.address)) || [];

    const instanceOptions = instances
        .filter(inst => {
            const privateIp = Object.values(inst.addresses || {}).flat().find((a: any) => a["OS-EXT-IPS:type"] === "fixed")?.addr;
            return privateIp && !currentMemberAddresses.includes(privateIp);
        })
        .map(inst => {
            let privateIp = '';
            let subnetId = '';
            
            for (const [netName, addrs] of Object.entries(inst.addresses || {})) {
                const fixed = (addrs as any[]).find((a: any) => a["OS-EXT-IPS:type"] === "fixed");
                if (fixed) {
                    privateIp = fixed.addr;
                    subnetId = fixed.subnet_id;
                    
                    if (!subnetId) {
                        const network = networks.find(n => n.name === netName || n.id === netName);
                        if (network) {
                            subnetId = network.subnet_id || '';
                        }
                    }
                    break;
                }
            }
            
            return {
                value: inst.id,
                label: `${inst.name} (${privateIp || 'No IP'})`,
                address: privateIp,
                subnetId: subnetId || inst.metadata?.subnet_id || (inst as any).subnet_id,
                securityGroupId: securityGroups.find(sg => inst.security_groups?.includes(sg.name))?.id
            };
        });

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={`Load Balancer Details: ${lb?.name || ''}`} size="lg">
            <div style={{ padding: '20px' }}>
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <FaSyncAlt className="spin" size={30}/>
                        <p>Loading details...</p>
                    </div>
                ) : lb ? (
                    <div>
                        {/*<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px' }}>*/}
                        {/*    <div>*/}
                        {/*        <p><strong>VIP Address:</strong> {lb.vip_address}</p>*/}
                        {/*        <p><strong>Status:</strong> {lb.operating_status}</p>*/}
                        {/*    </div>*/}
                        {/*    <div>*/}
                        {/*        <p><strong>Provisioning:</strong> {lb.provisioning_status}</p>*/}
                        {/*        <p><strong>Provider:</strong> {lb.provider}</p>*/}
                        {/*    </div>*/}
                        {/*</div>*/}

                        {/* Domains / DNS - point a *.redu.cloud name or your own verified domain at this LB */}
                        <div style={{ marginBottom: '30px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                <h3>Domains</h3>
                                <button
                                    className="btn-primary"
                                    style={{ background: 'var(--action-primary-bg)', color: 'var(--action-primary-text)', border: 'none', padding: '4px 12px', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '5px' }}
                                    onClick={() => setShowDnsAdd(v => { const nv = !v; if (nv) setDnsPort(lb?.listeners?.[0]?.protocol_port || 80); return nv; })}
                                >
                                    <FaPlus size={12}/> Add Domain
                                </button>
                            </div>
                            <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '0 0 10px' }}>
                                Point a domain at this load balancer: a free <code>.redu.cloud</code> name, or your own domain (verify ownership with a TXT record; point its A record at redu first).
                            </p>

                            {showDnsAdd && (
                                <div style={{ background: 'var(--surface-alt)', padding: '15px', borderRadius: '8px', marginBottom: '15px', border: '1px solid var(--border)' }}>
                                    <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                                        <div style={{ flex: '2 1 220px' }}>
                                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.8rem' }}>Domain</label>
                                            <div style={{ display: 'flex', gap: '6px' }}>
                                                <input
                                                    type="text"
                                                    value={dnsDomain}
                                                    onChange={e => { setDnsDomain(e.target.value); setVerifyStep('idle'); setChallenge(null); }}
                                                    placeholder="app.yourcompany.com, or leave empty for a .redu.cloud name"
                                                    style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid var(--border)', background: 'var(--surface-card)', color: 'var(--text-primary)' }}
                                                />
                                                <button type="button" onClick={generateDnsName} title="Generate a .redu.cloud name" style={{ padding: '8px 10px', borderRadius: '4px', border: '1px solid var(--border)', background: 'var(--surface-card)', color: 'var(--text-primary)', cursor: 'pointer' }}>⟳</button>
                                            </div>
                                        </div>
                                        <div style={{ flex: '1 1 90px' }}>
                                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.8rem' }}>Port</label>
                                            <input type="number" value={dnsPort} onChange={e => setDnsPort(parseInt(e.target.value) || 0)} style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid var(--border)', background: 'var(--surface-card)', color: 'var(--text-primary)' }}/>
                                        </div>
                                    </div>

                                    {isCustomDomain && verifyStep !== 'verified' && (
                                        <div style={{ marginTop: '12px', padding: '12px', borderRadius: '6px', background: 'var(--surface-card)', border: '1px solid var(--border)' }}>
                                            <p style={{ margin: '0 0 8px', fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: 600 }}>Custom domain: verify ownership first</p>
                                            {(verifyStep === 'idle' || verifyStep === 'failed') ? (
                                                <>
                                                    <p style={{ margin: '0 0 8px', fontSize: '0.78rem', color: 'var(--text-muted)' }}>Add a TXT record to prove you own <strong style={{ color: 'var(--text-primary)' }}>{dnsDomain.trim()}</strong>.</p>
                                                    <button type="button" onClick={getChallenge} style={{ padding: '6px 12px', fontSize: '0.8rem', borderRadius: '5px', border: '1px solid var(--border)', background: 'var(--surface-alt)', color: 'var(--text-primary)', cursor: 'pointer' }}>Get TXT record</button>
                                                    {verifyStep === 'failed' && <p style={{ margin: '6px 0 0', fontSize: '0.78rem', color: '#e53e3e' }}>TXT record not found yet. Save it and try again.</p>}
                                                </>
                                            ) : verifyStep === 'loading' ? (
                                                <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-muted)' }}>Loading…</p>
                                            ) : challenge ? (
                                                <>
                                                    <div style={{ fontSize: '0.78rem', marginBottom: '8px', lineHeight: 1.7 }}>
                                                        <div><strong>Type:</strong> <code>TXT</code></div>
                                                        <div style={{ wordBreak: 'break-all', cursor: 'pointer' }} title="Click to copy" onClick={() => { navigator.clipboard.writeText(challenge.record_name); showToast('Copied', 'success'); }}><strong>Name:</strong> <code>{challenge.record_name}</code></div>
                                                        <div style={{ wordBreak: 'break-all', cursor: 'pointer' }} title="Click to copy" onClick={() => { navigator.clipboard.writeText(challenge.record_value); showToast('Copied', 'success'); }}><strong>Value:</strong> <code>{challenge.record_value}</code></div>
                                                    </div>
                                                    {challenge.hint && <p style={{ margin: '0 0 8px', fontSize: '0.78rem', lineHeight: 1.5 }}>{challenge.hint}</p>}
                                                    <button type="button" onClick={verifyDomain} disabled={verifyStep === 'verifying'} style={{ padding: '6px 12px', fontSize: '0.8rem', borderRadius: '5px', border: 'none', background: 'var(--action-primary-bg)', color: 'var(--action-primary-text)', cursor: 'pointer', fontWeight: 600 }}>{verifyStep === 'verifying' ? 'Checking…' : 'Verify'}</button>
                                                </>
                                            ) : null}
                                        </div>
                                    )}
                                    {isCustomDomain && verifyStep === 'verified' && (
                                        <p style={{ margin: '10px 0 0', fontSize: '0.8rem', color: '#48bb78', fontWeight: 600 }}>✓ Domain verified</p>
                                    )}

                                    <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
                                        <button type="button" onClick={addDns} disabled={!canSubmitDns} style={{ padding: '8px 16px', borderRadius: '4px', border: 'none', background: canSubmitDns ? 'var(--action-primary-bg)' : 'var(--surface-hover)', color: canSubmitDns ? 'var(--action-primary-text)' : 'var(--text-muted)', cursor: canSubmitDns ? 'pointer' : 'not-allowed' }}>{dnsSubmitting ? 'Adding…' : 'Add Domain'}</button>
                                        <button type="button" onClick={resetDnsForm} style={{ padding: '8px 16px', borderRadius: '4px', border: '1px solid var(--border)', background: 'var(--surface-card)', color: 'var(--text-primary)', cursor: 'pointer' }}>Cancel</button>
                                    </div>
                                </div>
                            )}

                            <table className="keypairs-table">
                                <thead><tr><th>Domain</th><th>Port</th><th>Actions</th></tr></thead>
                                <tbody>
                                    {(lb.domains || []).map((d: any) => (
                                        <tr key={d.proxyId}>
                                            <td><a href={d.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', wordBreak: 'break-all' }}>{d.domain}</a></td>
                                            <td>{d.port}</td>
                                            <td onClick={(e) => e.stopPropagation()}>
                                                {d.removable === false ? (
                                                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }} title="This is the cluster's own URL, managed by the cluster">cluster-managed</span>
                                                ) : (
                                                    <button type="button" onClick={() => removeDns(d.proxyId)} style={{ padding: '3px 10px', fontSize: '0.78rem', borderRadius: '5px', border: '1px solid #e53e3e', background: 'transparent', color: '#e53e3e', cursor: 'pointer' }}>Remove</button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                    {(!lb.domains || lb.domains.length === 0) && (
                                        <tr><td colSpan={3} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No domains yet</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div style={{ marginBottom: '30px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                <h3>Members</h3>
                                {lb.tags?.includes('manual') && (
                                    <button 
                                        className="btn-primary" 
                                    style={{ background: 'var(--action-primary-bg)', color: 'var(--action-primary-text)', border: 'none', padding: '4px 12px', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '5px' }}
                                        onClick={() => setAddingMember(!addingMember)}
                                    >
                                        <FaPlus size={12}/> Add Member
                                    </button>
                                )}
                            </div>

                            {addingMember && (
                                <div style={{ background: 'var(--surface-alt)', padding: '15px', borderRadius: '8px', marginBottom: '15px', border: '1px solid var(--border)' }}>
                                    <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                                        <div style={{ flex: 2 }}>
                                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.8rem' }}>Instance</label>
                                            <Select
                                                options={instanceOptions}
                                                value={selectedInstance}
                                                onChange={(selected) => setSelectedInstance(selected)}
                                                placeholder="Select instance..."
                                            />
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.8rem' }}>Port</label>
                                            <input
                                                type="number"
                                                value={memberPort}
                                                onChange={(e) => setMemberPort(parseInt(e.target.value))}
                                                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid var(--border)', background: 'var(--surface-input)', color: 'var(--text-primary)' }}
                                            />
                                        </div>
                                        <button 
                                            className="btn-primary" 
                                            style={{ background: 'var(--action-primary-bg)', color: 'var(--action-primary-text)', border: 'none', padding: '8px 16px', borderRadius: '4px' }}
                                            onClick={handleAddMember}
                                            disabled={isSubmittingMember || !selectedInstance}
                                        >
                                            {isSubmittingMember ? 'Adding...' : 'Add'}
                                        </button>
                                    </div>
                                </div>
                            )}

                            <table className="keypairs-table">
                                <thead>
                                    <tr>
                                        <th>Address</th>
                                        <th>Port</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {lb.pools?.flatMap((pool: any) => 
                                        pool.members?.map((member: any) => {
                                            const instance = instances.find(inst => 
                                                Object.values(inst.addresses || {}).flat().some((addr: any) => 
                                                    addr.addr === member.address && addr["OS-EXT-IPS:type"] === "fixed"
                                                )
                                            );

                                            return (
                                                <tr 
                                                    key={member.id} 
                                                    onClick={() => instance && navigate(`/category/compute/instances/${instance.id}`)}
                                                    style={{ cursor: instance ? 'pointer' : 'default' }}
                                                >
                                                    <td>{member.address}</td>
                                                    <td>{member.protocol_port}</td>
                                                    <td>{member.operating_status}</td>
                                                    <td>
                                                        <div onClick={(e) => e.stopPropagation()} title={lb.tags?.includes('manual') ? "Actions" : "Cluster-managed members cannot be removed manually"}>
                                                            <Select
                                                                options={[
                                                                    {value: 'delete', label: 'Remove Member'}
                                                                ]}
                                                                onChange={(option) => {
                                                                    if (option?.value === 'delete') {
                                                                        handleRemoveMember(pool.id, member.id, member.address);
                                                                    }
                                                                }}
                                                                placeholder="Actions"
                                                                isSearchable={false}
                                                                isDisabled={!lb.tags?.includes('manual')}
                                                                menuPortalTarget={document.body}
                                                                styles={getActionSelectStyles()}
                                                            />
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                    {(!lb.pools || lb.pools.every((p: any) => !p.members?.length)) && (
                                        <tr><td colSpan={4} style={{ textAlign: 'center' }}>No members found</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <p>Load balancer not found.</p>
                )}
            </div>
        </Modal>
    );
};

export default LoadBalancerDetailsModal;
